import React, { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import Toast from "./Toast";
import ErrorMessage from "./ErrorMessage";
import { useToast } from "../hooks/useToast";
import {
  useAutomaticOperationsQuery,
  useTransactionsListValuesForSelectQuery,
  useCreateAutomaticOperationMutation,
  useEditAutomaticOperationMutation,
  useDeleteAutomaticOperationMutation,
  AutomaticOperationsQuery,
  TransactionsListValuesForSelectQuery,
} from "../generated/graphql";
import { splitAmount, formatAmountInput } from "../utils/transactionAmount";
import { extractPostIdFromComposite } from "../utils/idHelpers";
import { getAvailableAccounts } from "../utils/accountSelection";

type AutomaticOp =
  AutomaticOperationsQuery["groups"][number]["automaticOperations"][number];
type SelectGroup = TransactionsListValuesForSelectQuery["groups"][number];
type SelectUser = TransactionsListValuesForSelectQuery["users"][number];

type FormState = {
  groupId: string;
  accountId: string;
  contextId: string;
  postId: string;
  userId: string;
  amount: string;
  comment: string;
  nextDate: string;
  monthDelta: string;
};

type FormMode =
  | { type: "create" }
  | { type: "edit"; opId: string; groupId: string };

const emptyForm = (): FormState => ({
  groupId: "",
  accountId: "",
  contextId: "",
  postId: "",
  userId: "",
  amount: "",
  comment: "",
  nextDate: "",
  monthDelta: "1",
});

// --- FormPanel ---

interface FormPanelProps {
  form: FormState;
  mode: FormMode;
  groups: SelectGroup[];
  users: SelectUser[];
  onChange: (field: keyof FormState, value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}

const FormPanel: React.FC<FormPanelProps> = ({
  form,
  mode,
  groups,
  users,
  onChange,
  onSave,
  onCancel,
}) => {
  const selectedGroup = groups.find((g) => g.id === form.groupId);
  const accounts = getAvailableAccounts(
    { groups },
    form.groupId,
    form.userId || null,
    mode.type === "edit" ? form.accountId : undefined,
  );
  const contexts = selectedGroup?.contexts ?? [];
  const posts =
    contexts.find((c) => c.id === form.contextId)?.posts ?? [];

  const isCreate = mode.type === "create";
  const title = isCreate
    ? "Nouvelle opération automatique"
    : "Modifier l'opération";

  const inputClass =
    "w-full border border-gray-300 rounded-sm px-3 py-2 text-sm focus:outline-hidden focus:ring-2 focus:ring-blue-500";
  const labelClass = "block text-sm font-medium text-gray-700 mb-1";

  return (
    <div className="bg-white border border-blue-200 rounded-lg shadow-xs p-6 mb-6">
      <h2 className="text-lg font-semibold mb-4">{title}</h2>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
        {isCreate && (
          <div>
            <label className={labelClass}>Foyer</label>
            <select
              className={inputClass}
              value={form.groupId}
              onChange={(e) => onChange("groupId", e.target.value)}
            >
              <option value="">— Sélectionner —</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>
                  {g.name}
                </option>
              ))}
            </select>
          </div>
        )}
        <div>
          <label className={labelClass}>Compte</label>
          <select
            className={inputClass}
            value={form.accountId}
            onChange={(e) => onChange("accountId", e.target.value)}
            disabled={!form.groupId}
          >
            <option value="">— Sélectionner —</option>
            {accounts.map((a) => (
              <option key={a.id} value={a.id}>
                {a.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Catégorie</label>
          <select
            className={inputClass}
            value={form.contextId}
            onChange={(e) => onChange("contextId", e.target.value)}
            disabled={!form.groupId}
          >
            <option value="">— Sélectionner —</option>
            {contexts.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Poste</label>
          <select
            className={inputClass}
            value={form.postId}
            onChange={(e) => onChange("postId", e.target.value)}
            disabled={!form.contextId}
          >
            <option value="">— Sélectionner —</option>
            {posts.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Utilisateur (optionnel)</label>
          <select
            className={inputClass}
            value={form.userId}
            onChange={(e) => onChange("userId", e.target.value)}
          >
            <option value="">— Aucun —</option>
            {users.map((u) => (
              <option key={u.id} value={u.id}>
                {u.firstname}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelClass}>Montant (€)</label>
          <input
            type="number"
            step="0.01"
            className={inputClass}
            placeholder="-150 ou 50"
            value={form.amount}
            onChange={(e) => onChange("amount", e.target.value)}
          />
          <p className="text-xs text-gray-400 mt-0.5">
            Négatif = débit, positif = crédit
          </p>
        </div>
        <div>
          <label className={labelClass}>Commentaire</label>
          <input
            type="text"
            className={inputClass}
            placeholder="Ex : Loyer, EDF..."
            value={form.comment}
            onChange={(e) => onChange("comment", e.target.value)}
          />
        </div>
        <div>
          <label className={labelClass}>Prochaine date</label>
          <input
            type="date"
            className={inputClass}
            value={form.nextDate}
            onChange={(e) => onChange("nextDate", e.target.value)}
          />
        </div>
        <div>
          <label className={labelClass}>Fréquence (mois)</label>
          <input
            type="number"
            min="1"
            className={inputClass}
            placeholder="1"
            value={form.monthDelta}
            onChange={(e) => onChange("monthDelta", e.target.value)}
          />
        </div>
      </div>
      <div className="flex gap-3 mt-5">
        <button
          onClick={onSave}
          className="bg-blue-600 text-white px-5 py-2 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
        >
          Enregistrer
        </button>
        <button
          onClick={onCancel}
          className="bg-gray-200 text-gray-700 px-5 py-2 rounded-lg hover:bg-gray-300 transition-colors text-sm font-medium"
        >
          Annuler
        </button>
      </div>
    </div>
  );
};

// --- OperationRow ---

interface OperationRowProps {
  op: AutomaticOp;
  onEdit: () => void;
  onDelete: () => void;
}

const OperationRow: React.FC<OperationRowProps> = ({ op, onEdit, onDelete }) => {
  const displayAmount = formatAmountInput(op.credit, op.debit);
  const amountNum = parseFloat(displayAmount);

  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-3 text-sm text-gray-800">{op.comment}</td>
      <td
        className={`px-4 py-3 text-sm text-right font-medium ${
          amountNum >= 0 ? "text-green-600" : "text-red-600"
        }`}
      >
        {amountNum > 0 ? "+" : ""}
        {displayAmount} €
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {new Date(op.nextDate).toLocaleDateString("fr-FR")}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {op.monthDelta === 1 ? "Mensuel" : `Tous les ${op.monthDelta} mois`}
      </td>
      <td className="px-4 py-3 text-sm text-gray-600">{op.account.name}</td>
      <td className="px-4 py-3 text-sm text-gray-600">
        {op.context.name} / {op.post.name}
      </td>
      <td className="px-4 py-3 text-sm text-gray-500">
        {op.user?.firstname ?? "—"}
      </td>
      <td className="px-4 py-3 text-sm text-center whitespace-nowrap">
        <button
          onClick={onEdit}
          className="p-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-md focus:outline-hidden focus:ring-2 focus:ring-blue-500 transition-colors mr-3"
          title="Modifier"
        >
          ✏
        </button>
        <button
          onClick={onDelete}
          className="p-1 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-md focus:outline-hidden focus:ring-2 focus:ring-red-500 transition-colors"
          title="Supprimer"
        >
          🗑
        </button>
      </td>
    </tr>
  );
};

// --- Main component ---

const AutomaticOperations: React.FC = () => {
  const { isAuthenticated } = useAuth0();
  const [formMode, setFormMode] = useState<FormMode | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const { toast, setToast } = useToast();

  const { data, loading, error, refetch } = useAutomaticOperationsQuery({
    skip: !isAuthenticated,
    fetchPolicy: "cache-and-network",
  });

  const { data: selectData } = useTransactionsListValuesForSelectQuery({
    skip: !isAuthenticated,
  });

  const [createOp] = useCreateAutomaticOperationMutation();
  const [editOp] = useEditAutomaticOperationMutation();
  const [deleteOp] = useDeleteAutomaticOperationMutation();

  const handleFormChange = (field: keyof FormState, value: string) => {
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "groupId") {
        next.accountId = "";
        next.contextId = "";
        next.postId = "";
      }
      if (field === "userId") next.accountId = "";
      if (field === "contextId") next.postId = "";
      return next;
    });
  };

  const openCreate = () => {
    setForm(emptyForm());
    setFormMode({ type: "create" });
  };

  const openEdit = (groupId: string, op: AutomaticOp) => {
    setForm({
      groupId,
      accountId: op.account.id,
      contextId: op.context.id,
      postId: op.post.id,
      userId: op.user?.id ?? "",
      amount: formatAmountInput(op.credit, op.debit),
      comment: op.comment,
      nextDate: new Date(op.nextDate).toISOString().substring(0, 10),
      monthDelta: String(op.monthDelta),
    });
    setFormMode({ type: "edit", opId: op.id, groupId });
  };

  const closeForm = () => {
    setFormMode(null);
    setForm(emptyForm());
  };

  const handleSave = async () => {
    const { credit, debit } = splitAmount(form.amount);
    const commonVars = {
      credit,
      debit,
      comment: form.comment,
      nextDate: form.nextDate,
      monthDelta: parseInt(form.monthDelta, 10),
      accountId: form.accountId,
      contextId: form.contextId,
      postId: form.postId ? extractPostIdFromComposite(form.postId) : form.postId,
      userId: form.userId || null,
    };

    try {
      if (formMode?.type === "create") {
        await createOp({
          variables: { groupId: form.groupId, ...commonVars },
        });
        setToast({ message: "Opération créée avec succès", type: "success" });
      } else if (formMode?.type === "edit") {
        await editOp({
          variables: {
            id: formMode.opId,
            groupId: formMode.groupId,
            ...commonVars,
          },
        });
        setToast({
          message: "Opération modifiée avec succès",
          type: "success",
        });
      }
      closeForm();
    } catch {
      setToast({ message: "Erreur lors de l'enregistrement", type: "error" });
    }
  };

  const handleDelete = async (groupId: string, id: string) => {
    if (!window.confirm("Supprimer cette opération automatique ?")) return;
    try {
      await deleteOp({ variables: { groupId, id } });
      setToast({ message: "Opération supprimée", type: "success" });
    } catch {
      setToast({ message: "Erreur lors de la suppression", type: "error" });
    }
  };

  if (!isAuthenticated)
    return (
      <div className="text-gray-500">
        Connectez-vous pour voir vos opérations automatiques.
      </div>
    );
  if (loading) return <div className="text-center text-gray-500">Chargement...</div>;
  if (error) return <ErrorMessage error={error} onRetry={refetch} />;

  const groups = data?.groups ?? [];
  const groupsWithOps = groups.filter((g) => g.automaticOperations.length > 0);

  return (
    <div className="container mx-auto">
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(null)}
        />
      )}

      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Opérations automatiques</h1>
        {!formMode && (
          <button
            onClick={openCreate}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            + Nouvelle opération
          </button>
        )}
      </div>

      {formMode && (
        <FormPanel
          form={form}
          mode={formMode}
          groups={selectData?.groups ?? []}
          users={selectData?.users ?? []}
          onChange={handleFormChange}
          onSave={handleSave}
          onCancel={closeForm}
        />
      )}

      {groupsWithOps.length === 0 && !formMode && (
        <div className="bg-white border border-dashed border-gray-300 rounded-lg p-8 text-center text-gray-500">
          <p className="text-lg font-medium mb-2">
            Aucune opération automatique
          </p>
          <p className="text-sm text-gray-400">
            Créez votre première opération avec le bouton ci-dessus.
          </p>
        </div>
      )}

      {groupsWithOps.map((group) => (
        <div key={group.id} className="mb-8">
          <h2 className="text-lg font-semibold mb-3 text-gray-700 border-b pb-2">
            {group.name}{" "}
            <span className="text-sm font-normal text-gray-400">
              ({group.automaticOperations.length} opération
              {group.automaticOperations.length > 1 ? "s" : ""})
            </span>
          </h2>
          <div className="overflow-x-auto">
            <table className="w-full bg-white rounded-lg shadow-xs border border-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Commentaire
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Montant
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prochaine date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fréquence
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Compte
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Catégorie / Poste
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilisateur
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {group.automaticOperations.map((op) => (
                  <OperationRow
                    key={op.id}
                    op={op}
                    onEdit={() => openEdit(group.id, op)}
                    onDelete={() => handleDelete(group.id, op.id)}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default AutomaticOperations;
